import _head from 'lodash/head';
import { GraphQLError } from 'graphql';
import {
  storageForwardedRequestTokenData,
  storageInviteToken,
  storageProfileAccessToken,
} from 'config/constants/config';

interface GraphQLErrorWithErrorData extends GraphQLError {
  data?: {
    errorCode?: number;
    errorData?: {
      statusCode?: string;
      loginAttemptCount?: number;
    };
  };
  extensions: {
    exception?: {
      publicData?: {
        statusCode: string;
        loginAttemptCount: number;
      };
      errorCode?: number;
    };
  };
}

type ErrorDataWithStatusCodeType = {
  graphQLErrors: readonly GraphQLErrorWithErrorData[];
};

export const getCustomError = (error: ErrorDataWithStatusCodeType): { statusCode?: string; attemptCount?: number } => {
  const errorObject = error?.graphQLErrors && !!error.graphQLErrors.length && _head(error.graphQLErrors);
  const exception = errorObject?.extensions?.exception;

  if (exception?.publicData?.statusCode) {
    return { statusCode: exception.publicData.statusCode };
  }

  if (exception.errorCode === 4) {
    return { attemptCount: exception?.publicData?.loginAttemptCount || -1 };
  }

  return null;
};

// if redirectUrl param in url search is changed - update localstorage request-token
const getToken = (tokenType: string, urlArray: string[]): string => urlArray[urlArray.indexOf(tokenType) + 1];

export const updateLocalStorageRequestToken = (url: string): void => {
  const urlParts = url?.split('/');
  const isInvite = urlParts?.includes('roster-invite');
  const isProfileAccess = urlParts?.includes('accept-profile-access');
  const forwardedRequestTypes = ['request', 'media-request', 'audition-invite', 'callback-invite'];
  const forwardedRequestType = forwardedRequestTypes.filter((requestType) => urlParts?.includes(requestType))[0];

  if (isInvite || isProfileAccess || forwardedRequestType) {
    if (isInvite) localStorage.setItem(storageInviteToken, getToken('roster-invite', urlParts));

    if (isProfileAccess) localStorage.setItem(storageProfileAccessToken, getToken('accept-profile-access', urlParts));

    if (forwardedRequestType) {
      localStorage.setItem(
        storageForwardedRequestTokenData,
        JSON.stringify({ type: forwardedRequestType, token: getToken(forwardedRequestType, urlParts) })
      );
    }
  } else if (!isInvite && localStorage.getItem(storageInviteToken)) {
    localStorage.removeItem(storageInviteToken);
  } else if (!isProfileAccess && localStorage.getItem(storageProfileAccessToken)) {
    localStorage.removeItem(storageProfileAccessToken);
  } else if (!forwardedRequestType && localStorage.getItem(storageForwardedRequestTokenData)) {
    localStorage.removeItem(storageForwardedRequestTokenData);
  }
};

// on handle submit b21 login - if we have rosterInviteToken or profileAccessToken in storage -> remove them as no need
export const removeLocalStorageInviteOrAccessTokens = (): void => {
  // if we have rosterInviteToken or profileAccessToken in storage -> remove them as no need
  if (localStorage.getItem(storageInviteToken)) {
    localStorage.removeItem(storageInviteToken);
  } else if (localStorage.getItem(storageProfileAccessToken)) {
    localStorage.removeItem(storageProfileAccessToken);
  } else if (localStorage.getItem(storageForwardedRequestTokenData)) {
    localStorage.removeItem(storageForwardedRequestTokenData);
  }
};

// possible request pages
const TALENTS_URLS = [
  '/ROSTER-INVITE/',
  '/ACCEPT-PROFILE-ACCESS/',
  '/REQUEST/',
  '/MEDIA-REQUEST/',
  '/AUDITION-INVITE/',
  '/CALLBACK-INVITE/',
  '/PROJECT',
];

type SearchParamsTypes = {
  redirectUrl?: string;
  emailVerified?: string;
  rosterInvite?: string;
  audienceToken?: string;
  redirectFromTalent?: string;
  onBoardingError?: string;
};

export function getQueryParamsFromSearch<S extends SearchParamsTypes>(searchString: string): S {
  const queryParams = new URLSearchParams(searchString);
  const redirectUrl = queryParams.get('redirectUrl');
  const matchedTalentRoute = TALENTS_URLS.find((talentUrl) => String(redirectUrl).toUpperCase().startsWith(talentUrl));
  const redirectFromTalent =
    String(queryParams.get('redirectAudience')).toUpperCase() === 'TALENT' && Boolean(matchedTalentRoute);

  const result = Object.fromEntries([...queryParams]);

  return {
    ...result,
    redirectFromTalent,
  } as S & SearchParamsTypes;
}

export enum ERROR_CODES {
  ACCESS_DENIED = 'access_denied',
  TEMPORARILY_UNAVAILABLE = 'temporarily_unavailable',
}
